
import React from 'react';
import { Link } from 'react-router-dom';
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AlertCircle, AlertOctagon } from "lucide-react";
import { PointsDeductionDialog } from '../admin/PointsDeductionDialog';
import { FormDisplay, getLastSixForm } from './FormDisplay';
import { PPGDisplay } from './PPGDisplay';
import { TeamRecord } from '../types';

interface TeamTableRowProps {
  record: TeamRecord;
  index: number;
  viewType: 'all' | 'home' | 'away';
  data: any[];
  year: string;
  league: string;
  isAdmin: boolean;
  hasDeduction: boolean;
  onStatClick: (team: string, type: 'wins' | 'draws' | 'losses') => void;
  onFormClick: (team: string, match: any) => void;
}

export const TeamTableRow: React.FC<TeamTableRowProps> = ({
  record,
  index,
  viewType,
  data,
  year,
  league,
  isAdmin,
  hasDeduction,
  onStatClick,
  onFormClick
}) => {
  return (
    <TableRow className="h-10">
      <TableCell className="py-1 px-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{index + 1}</span>
          <Link 
            to={`/team/${record.team}`}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {hasDeduction && <span className="text-red-500">*</span>}{record.team}
          </Link>
          {isAdmin && (
            <PointsDeductionDialog 
              team={record.team} 
              season={year}
              league={league}
            />
          )}
        </div>
      </TableCell>
      <TableCell className="py-1 px-2">
        <FormDisplay 
          matches={getLastSixForm(data, record.team, viewType)}
          team={record.team}
          onMatchClick={onFormClick}
        />
      </TableCell>
      <TableCell className="text-center py-1 px-2">{record.played}</TableCell>
      <TableCell className="text-center py-1 px-2">
        <Button 
          variant="ghost" 
          className="px-2 h-6 hover:bg-blue-100 text-sm"
          onClick={() => onStatClick(record.team, 'wins')}
        >
          {record.wins}
        </Button>
      </TableCell>
      <TableCell className="text-center py-1 px-2">
        <Button 
          variant="ghost" 
          className="px-2 h-6 hover:bg-blue-100 text-sm"
          onClick={() => onStatClick(record.team, 'draws')}
        >
          {record.draws}
        </Button>
      </TableCell>
      <TableCell className="text-center py-1 px-2">
        <Button 
          variant="ghost" 
          className="px-2 h-6 hover:bg-blue-100 text-sm"
          onClick={() => onStatClick(record.team, 'losses')}
        >
          {record.losses}
        </Button>
      </TableCell>
      <TableCell className="text-center py-1 px-2">{record.goalsFor}</TableCell>
      <TableCell className="text-center py-1 px-2">{record.goalsAgainst}</TableCell>
      <TableCell className="text-center py-1 px-2">{record.goalDifference}</TableCell>
      <TableCell className="text-center py-1 px-2 font-bold">{record.points}</TableCell>
      <TableCell className="text-center py-1 px-2">
        <PPGDisplay ppg={record.ppg} />
      </TableCell>
      <TableCell className="text-center py-1 px-2 text-sm">
        {record.cleanSheets} ({record.cleanSheetsPercentage.toFixed(1)}%)
      </TableCell>
      <TableCell className="text-center py-1 px-2 text-sm">
        {record.btts} ({record.bttsPercentage.toFixed(1)}%)
      </TableCell>
      <TableCell className="text-center py-1 px-2 text-sm">
        {record.over15} ({record.over15Percentage.toFixed(1)}%)
      </TableCell>
      <TableCell className="text-center py-1 px-2 text-sm">
        {record.over25} ({record.over25Percentage.toFixed(1)}%)
      </TableCell>
      <TableCell className="text-center py-1 px-2">
        <div className="flex items-center justify-center gap-1 text-sm">
          <span className="flex items-center">
            <AlertCircle className="h-3 w-3 text-yellow-500 mr-0.5" />
            {(record.yellowCards / record.played).toFixed(1)}
          </span>
          <span>/</span>
          <span className="flex items-center">
            <AlertOctagon className="h-3 w-3 text-red-500 mr-0.5" />
            {(record.redCards / record.played).toFixed(1)}
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
};
